import { ChevronRight, TriangleAlert } from "lucide-react";
import { agentMap, toneSurface } from "@/lib/mockData";
import StatusPill from "./StatusPill";
import PriorityPill from "./PriorityPill";
import { cn } from "@/lib/utils";

const fmtDeadline = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const relDays = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.round(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days < 0) return `${-days}d overdue`;
  return `in ${days}d`;
};

export default function TaskTable({ tasks, onOpen }) {
  if (!tasks.length) {
    return (
      <div
        data-testid="task-table-empty"
        className="card-soft rounded-[28px] p-10 text-center text-[13px] text-zinc-500"
      >
        No tasks match this filter.
      </div>
    );
  }

  return (
    <div data-testid="task-table" className="card-soft overflow-hidden rounded-[28px]">
      {/* Desktop / tablet header — hidden on mobile */}
      <div className="hidden grid-cols-[140px_1fr_180px_100px_140px_36px] gap-4 border-b border-black/[0.04] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400 md:grid">
        <div>Status</div>
        <div>Title</div>
        <div>Owner</div>
        <div>Priority</div>
        <div>Deadline</div>
        <div />
      </div>

      <div className="divide-y divide-black/[0.04]">
        {tasks.map((t) => {
          const isHuman = t.owner === "HUMAN";
          const ownerAgent = agentMap[t.owner];
          const tone = toneSurface[ownerAgent?.tone] ?? toneSurface.neutral;
          const isDarkAvatar = ownerAgent?.tone === "dark";

          return (
            <button
              key={t.id}
              type="button"
              data-testid={`task-row-${t.id}`}
              data-task-owner={isHuman ? "HUMAN" : "agent"}
              onClick={() => onOpen(t.id)}
              className={cn(
                "group relative flex w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-zinc-50 md:grid md:grid-cols-[140px_1fr_180px_100px_140px_36px] md:items-center md:gap-4 md:px-5 md:py-3.5",
                isHuman && "bg-[#FFF7EE]/60 hover:bg-[#FFF7EE]",
              )}
            >
              {isHuman && (
                <span
                  className="absolute left-0 top-0 h-full w-[3px] bg-orange-500"
                  aria-hidden
                />
              )}

              {/* Title row — first on mobile, second column on desktop */}
              <div className="order-1 min-w-0 md:order-2">
                <div className="line-clamp-1 text-[14px] font-bold text-zinc-900 md:truncate md:text-[13.5px]">
                  {t.title}
                </div>
                <div className="line-clamp-2 text-[11.5px] text-zinc-400 md:truncate">
                  {t.description}
                </div>
              </div>

              {/* Status pill — mobile shows in a chips row */}
              <div className="order-2 flex flex-wrap items-center gap-2 md:order-1 md:flex-nowrap">
                <StatusPill status={t.status} />
                {/* Owner chip on mobile (compact) */}
                <span className="md:hidden">
                  {isHuman ? (
                    <span
                      data-testid={`task-row-${t.id}-human-chip`}
                      className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700"
                    >
                      <TriangleAlert className="h-3 w-3" strokeWidth={2.6} />
                      Human
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
                      <span
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-extrabold",
                          isDarkAvatar
                            ? "bg-[#0B0B0E] text-white"
                            : cn(tone.bg, "text-zinc-900"),
                        )}
                      >
                        {ownerAgent?.name?.[0]}
                      </span>
                      {ownerAgent?.name}
                    </span>
                  )}
                </span>
                <PriorityPill priority={t.priority} />
                <span className="text-[11px] font-semibold text-zinc-500 md:hidden">
                  {fmtDeadline(t.deadline)} · {relDays(t.deadline)}
                </span>
              </div>

              {/* Desktop-only owner column */}
              <div className="hidden min-w-0 items-center gap-2 md:order-3 md:flex">
                {isHuman ? (
                  <span
                    data-testid={`task-row-${t.id}-human-chip-md`}
                    className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700"
                  >
                    <TriangleAlert className="h-3 w-3" strokeWidth={2.6} />
                    Human
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold",
                        isDarkAvatar
                          ? "bg-[#0B0B0E] text-white"
                          : cn(tone.bg, "text-zinc-900"),
                      )}
                      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
                    >
                      {ownerAgent?.name?.[0]}
                    </span>
                    <span className="truncate text-[12.5px] font-semibold text-zinc-700">
                      {ownerAgent?.name}
                    </span>
                  </>
                )}
              </div>

              {/* Desktop-only priority column (already shown on mobile in chip row) */}
              <div className="hidden md:order-4 md:block">
                <PriorityPill priority={t.priority} />
              </div>

              {/* Desktop-only deadline column */}
              <div className="hidden text-[12px] leading-tight md:order-5 md:block">
                <div className="font-semibold text-zinc-800">
                  {fmtDeadline(t.deadline)}
                </div>
                <div className="text-[11px] text-zinc-400">{relDays(t.deadline)}</div>
              </div>

              {/* Chevron — desktop only */}
              <div className="hidden justify-end md:order-6 md:flex">
                <ChevronRight
                  className="h-4 w-4 text-zinc-400 transition-transform group-hover:translate-x-0.5 group-hover:text-zinc-700"
                  strokeWidth={2.2}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
